<script lang="ts">
  import { Icon } from './';
  import _ from '@centerhealth/common/lodash';
  import { InputValidation } from '@centerhealth/fe/center-kit';
  import { ResponseError, ValidationError } from '@centerhealth/fe/center-kit';
  import { GenericValidationSchema } from '@centerhealth/fe/validation';
  import { IValidationSchemaWithEndpoint } from '@centerhealth/types';
  import { FormEventHandler } from '@centerhealth/fe/form';

  let showPassword = false;
  export let value = '';
  export let label = '';
  export let error: ValidationError | ResponseError = {};

  // Assigning model type depending on name of the form.
  // Objective is to eliminate ts-warning for different form types.

  let modelLogin: IValidationSchemaWithEndpoint;
  let modelOthers: GenericValidationSchema;
  export let validationModel = label === 'Password' ? modelLogin : modelOthers;
  export let name = 'password';
  export let caption = '';

  const validation = new InputValidation(validationModel, name);
  const mainLabel = label || validation.label;

  // {name:{message}} -- Validation error type
  // {data:{errors:{name:{message}}}} -- Response error type
  // Check for property [message] (Assume the error is a validation error otherwise check as if the error is a response error)
  $: errorCaption = InputValidation.getErrorCaption(error, name);

  // The input hasError if there is an error caption
  $: hasError = _.isDefined(errorCaption);

  // Typing the onInput
  const onInput = (e: FormEventHandler<HTMLTextAreaElement>) => {
    value = e.target.value;
    // Edge case, If the user hit submit without filling in the input, Make sure to count that
    if (_.size(error) !== 0 && !validation.isDirty) validation.isDirty = true;
    error = validation.validateInput(value);
  };
</script>

<div class="input-wrapper mb-3">
  {#if mainLabel}
    <span class="block text-gray font-semibold mb-1" aria-label={mainLabel}>{mainLabel}</span>
  {/if}
  <div class="input-inner-wrapper relative">
    <span class="affix absolute left-3">
      <Icon name="lock" size="small" />
    </span>
    <input
      class="center-input"
      class:error-boundary={hasError}
      id={name}
      data-testid={name}
      type={showPassword ? 'text' : 'password'}
      placeholder=""
      {value}
      {name}
      on:input={onInput}
      on:blur={validation.onBlur}
    />
    <div role="button" class="reveal-button" on:click={() => (showPassword = !showPassword)}>
      {showPassword ? 'HIDE' : 'SHOW'}
    </div>
  </div>
  {#if hasError || caption}
    <span class={`block caption ${hasError ? 'text-red' : 'text-gray-dark'}`}>
      <p>{@html hasError ? errorCaption : caption}</p>
    </span>
  {/if}
</div>

<style lang="postcss">
  input:-webkit-autofill,
  input:-webkit-autofill:focus {
    transition: background-color 600000s 0s, color 600000s 0s;
  }
  .center-input {
    border-color: #e1e4e6;
    transition: border 0.2s;
    color: #555;
    @apply p-2 w-full text-base border-solid rounded-lg border-[1px];
  }
  .center-input:focus {
    outline: none;
    @apply border-blue-light border-solid;
  }
  .error-boundary,
  .error-boundary:focus {
    outline: none;
    border-width: 2px;
    @apply border-solid border-red;
  }
  .input-wrapper .center-input:not(:last-child) {
    padding-right: 60px;
  }

  .input-wrapper .center-input:not(:first-child) {
    padding-left: 45px;
  }
  .affix {
    height: 24px;
    width: fit-content;
    top: 11px;
    z-index: 2;
    @apply flex justify-center;
  }
  .caption {
    margin-top: 5px;
    @apply font-semibold text-sm;
  }
  .reveal-button {
    color: #a9b1b9;
    width: fit-content;
    top: 11px;
    z-index: 2;
    padding: 5px;
    letter-spacing: 1px;
    @apply absolute bg-white right-4 text-xs font-semibold;
  }
</style>
